import { useEffect, useMemo, useState, useRef } from 'react';
import { logoPrincipal } from '../assets';

export default function MasonryGallery({ items = [] }) {
  // ✅ Slider solo en teléfono (sm y arriba sigue masonry)
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const imageRef = useRef(null);

  const total = items.length;
  const canSlide = total > 1;

  const prev = () => {
    if (!canSlide) return;
    setActive((v) => (v - 1 + total) % total);
  };

  const next = () => {
    if (!canSlide) return;
    setActive((v) => (v + 1) % total);
  };

  const openLightbox = (idx) => {
    setActive(idx);
    setLightboxOpen(true);
    setZoom(1);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoom(1);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX;
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0) prev();
      else next();
    }
    setTouchStartX(null);
  };

  const [touchStartX, setTouchStartX] = useState(null);

  const activeItem = useMemo(() => items[active], [items, active]);

  // ✅ Por si items cambia (evita índices fuera de rango)
  useEffect(() => {
    if (active > total - 1) setActive(0);
  }, [total, active]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [lightboxOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      switch (e.key) {
        case 'ArrowLeft':
          prev();
          break;
        case 'ArrowRight':
          next();
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, canSlide]);

  return (
    <>
      <section className="py-24 relative overflow-visible">
        {/* ✅ Logo de fondo en toda la sección (mosaico repetido) */}
        <div
          className="absolute inset-0 pointer-events-none z-0 opacity-[0.06] dark:opacity-[0.10]"
          style={{
            backgroundImage: `url(${logoPrincipal})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '420px 420px',
            backgroundPosition: 'center',
            filter: 'grayscale(1) contrast(1.05)',
          }}
        />
        <div className="absolute inset-0 bg-white/10 dark:bg-black/20 pointer-events-none z-0" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* ✅ Mobile: slider SUPERPUESTO (NO renderiza una lista vertical) */}
          <div className="sm:hidden">
            {total === 0 ? null : (
              <div className="relative">
                {/* Contenedor con altura fija */}
                <div
                  className="
                    relative w-full
                    rounded-2xl
                    bg-white/60 dark:bg-slate-950/40
                    border border-white/40 dark:border-white/10
                    shadow-[0_14px_40px_rgba(2,6,23,0.10)]
                    overflow-hidden
                  "
                  style={{ height: '68vh', maxHeight: 520 }}
                >
                  {/* ✅ SOLO una imagen visible (evita que el browser reserve espacio para otras) */}
                  <img
                    key={activeItem?.src || active}
                    src={activeItem?.src}
                    alt={activeItem?.alt ?? `Imagen ${active + 1}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    onClick={() => openLightbox(active)}
                  />

                  {activeItem?.caption ? (
                    <div className="absolute left-0 right-0 bottom-0 p-4 text-sm text-white bg-gradient-to-t from-black/70 via-black/20 to-transparent">
                      {activeItem.caption}
                    </div>
                  ) : null}
                </div>

                {/* Flechas */}
                {canSlide ? (
                  <>
                    <button
                      type="button"
                      onClick={prev}
                      aria-label="Anterior"
                      className="
                        absolute left-3 top-1/2 -translate-y-1/2
                        w-11 h-11 rounded-2xl
                        bg-white/70 dark:bg-slate-950/50
                        border border-white/45 dark:border-white/10
                        backdrop-blur-md
                        shadow-[0_10px_30px_rgba(2,6,23,0.18)]
                        text-slate-800 dark:text-slate-100
                        flex items-center justify-center
                        active:scale-95 transition
                      "
                    >
                      <span className="material-symbols-outlined">chevron_left</span>
                    </button>

                    <button
                      type="button"
                      onClick={next}
                      aria-label="Siguiente"
                      className="
                        absolute right-3 top-1/2 -translate-y-1/2
                        w-11 h-11 rounded-2xl
                        bg-white/70 dark:bg-slate-950/50
                        border border-white/45 dark:border-white/10
                        backdrop-blur-md
                        shadow-[0_10px_30px_rgba(2,6,23,0.18)]
                        text-slate-800 dark:text-slate-100
                        flex items-center justify-center
                        active:scale-95 transition
                      "
                    >
                      <span className="material-symbols-outlined">chevron_right</span>
                    </button>

                    {/* Indicador */}
                    <div className="mt-3 flex items-center justify-center gap-2">
                      {items.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setActive(i)}
                          aria-label={`Ir a imagen ${i + 1}`}
                          className={[
                            'h-2.5 rounded-full transition-all',
                            i === active ? 'w-8 bg-slate-900/70 dark:bg-white/70' : 'w-2.5 bg-slate-400/40 dark:bg-white/20',
                          ].join(' ')}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* ✅ Tablet/Desktop: masonry original */}
          <div className="hidden sm:block">
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6">
              {items.map((item, idx) => (
                <figure
                  key={idx}
                  className="
                    mb-6 break-inside-avoid overflow-hidden rounded-2xl
                    bg-white/60 dark:bg-slate-950/40
                    border border-white/40 dark:border-white/10
                    shadow-[0_14px_40px_rgba(2,6,23,0.10)]
                    transition-transform duration-300 hover:-translate-y-1
                  "
                >
                  <img
                    src={item.src}
                    alt={item.alt ?? `Imagen ${idx + 1}`}
                    loading="lazy"
                    className="w-full h-auto object-cover cursor-pointer"
                    onClick={() => openLightbox(idx)}
                  />
                  {item.caption ? (
                    <figcaption className="p-4 text-sm text-slate-600 dark:text-slate-300">{item.caption}</figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ✅ Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Vista ampliada"
          onClick={closeLightbox}
        >
          <div
            className="relative w-full max-w-3xl flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Contenedor de la imagen */}
            <div className="rounded-3xl overflow-hidden border border-white/20 shadow-[0_20px_100px_rgba(0,0,0,0.8)] bg-black w-full">
              <img
                ref={imageRef}
                src={activeItem?.src}
                alt={activeItem?.alt ?? `Imagen ${active + 1}`}
                className="w-full h-auto max-h-[70vh] object-contain transition-transform duration-200"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
                onWheel={handleWheel}
              />
            </div>

            {/* Caption */}
            {activeItem?.caption ? (
              <p className="mt-4 text-sm text-white/70 text-center max-w-2xl">{activeItem.caption}</p>
            ) : null}

            {/* Botón de cerrar prominente en parte inferior */}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={closeLightbox}
                className="
                  px-8 py-3 rounded-full
                  bg-gradient-to-r from-lime-400 to-lime-500
                  hover:from-lime-300 hover:to-lime-400
                  text-slate-950 font-bold
                  flex items-center gap-2
                  transition-all duration-300
                  shadow-[0_10px_30px_rgba(163,230,53,0.3)]
                  hover:shadow-[0_15px_40px_rgba(163,230,53,0.4)]
                "
              >
                <span className="material-symbols-outlined text-lg">close</span>
                <span>Cerrar</span>
              </button>
            </div>

            {canSlide ? (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Anterior"
                  className="
                    absolute left-4 top-1/3
                    w-12 h-12 rounded-full
                    bg-white/15 hover:bg-white/30
                    border border-white/30
                    text-white
                    flex items-center justify-center
                    transition-all duration-200
                  "
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>

                <button
                  type="button"
                  onClick={next}
                  aria-label="Siguiente"
                  className="
                    absolute right-4 top-1/3
                    w-12 h-12 rounded-full
                    bg-white/15 hover:bg-white/30
                    border border-white/30
                    text-white
                    flex items-center justify-center
                    transition-all duration-200
                  "
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}